import PageTitle from "../../../components/PageTitle";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { httprequest } from "../../../data/api";
import useStyles from "../styles";
import Calendar from "../../../components/Calendar";
import useWindowSize from "../../../hooks/useWindow";
import PopModal from "../../../components/PopupModal/PopModal";
import Layout from "../../../components/Layout";
import { useWard } from "../../../context/GaurdianContext";

const PERIOD = "/api/v1/period";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const StudentTimeTable = () => {
  const classes = useStyles();
  const { getCurrentClassId, selectedWard } = useWard();
  const [activeTerm, setActiveTerm] = useState([]);
  const [periodData, setPeriodData] = useState([]);
  const [timeConfig, setTimeConfig] = useState({
    slotMinTime: "07:00:00",
    slotMaxTime: "19:00:00"
  });
  const width = useWindowSize();
  const mobile = width < 576;

  /**
   * Determines the calendar view mode based on screen size.
   * @returns {string} Returns "timeGridDay" for mobile and "timeGridWeek" for desktop.
   */
  const renderView = () => {
    if (mobile) {
      return "timeGridDay";
    } else {
      return "timeGridWeek";
    }
  };

  /**
   * handle Get all Terms
   */
  const getAllTerms = () => {
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        const currentTerm = res?.data?.data.find(
          (active_term) => active_term?.is_current === true
        );
        setActiveTerm(currentTerm);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
   * Fetches all period.
   */
  const getAllPeriods = () => {
    const classId = getCurrentClassId();

    httprequest
      .get(`${PERIOD}?class_id=${classId}`)
      .then((res) => {
        setPeriodData(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  /**
   * Validates and normalizes time strings to ensure they're within reasonable school hours
   */
  const normalizeTime = (timeString) => {
    if (!timeString) return "07:00:00";

    const [hours, minutes, seconds] = timeString.split(":").map(Number);

    // If time is outside reasonable school hours (6 AM - 8 PM), adjust it
    if (hours < 6) {
      return `07:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    if (hours >= 20) {
      return `16:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    return timeString;
  };

  /**
   * Generates calendar events from the period data within the active term date range.
   *
   * @returns {Array<Object>} Array of event objects for calendar rendering.
   */
  const generateEvents = () => {
    const allEvents = [];

    // Check if we have the necessary data
    if (!activeTerm?.start_date || !activeTerm?.end_date || !periodData?.data?.days) {
      return allEvents;
    }

    const startDate = new Date(activeTerm.start_date);
    const endDate = new Date(activeTerm.end_date);
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const weekDay = currentDate
        .toLocaleDateString("en-US", { weekday: "short" })
        .toLowerCase();

      periodData.data.days.forEach((day) => {
        if (weekDay === day?.day) {
          day?.periods?.forEach((periodItem) => {
            // Normalize times to ensure they're within reasonable bounds
            const normalizedStartTime = normalizeTime(periodItem?.start_time);
            const normalizedEndTime = normalizeTime(periodItem?.end_time);

            // Create event date string
            const dateString = currentDate.toISOString().split("T")[0];

            const event = {
              id: periodItem?.id,
              type: periodItem?.type,
              title: periodItem?.subject?.name || periodItem?.title || "No Title",
              start: `${dateString}T${normalizedStartTime}`,
              end: `${dateString}T${normalizedEndTime}`,
              teacher: periodItem?.teachers?.[0] ?
                `${periodItem.teachers[0].first_name} ${periodItem.teachers[0].last_name}` :
                "No Teacher",
              subject_name: periodItem?.subject?.name,
              subject_id: periodItem?.subject?.id,
              day: day?.day,
              class: periodData?.data?.class?.name,
              class_id: periodData?.data?.class?.id,
              term: periodData?.data?.term?.name,
              term_id: periodData?.data?.term?.id,
              period_id: periodItem?.id,
              className: "general-events"
            };
            allEvents.push(event);
          });
        }
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allEvents;
  };

  const events = generateEvents();

  /**
   * Renders content for each calendar event.
   *
   * @param {Object} eventInfo - Event information from the calendar.
   * @param {boolean} isMobile - Whether the device is mobile.
   * @returns {JSX.Element} Rendered event content.
   */
  const renderEventContent = (eventInfo, isMobile) => {
    return (
      <div className={isMobile ? "rotated-vertical" : "normal-horizontal"}>
        <p className="no-margin-block padding-inline-10">
          {eventInfo.event?.title} | {eventInfo?.event?.extendedProps?.class}
        </p>
        <p className="no-margin-block padding-inline-10">
          {eventInfo.event?.extendedProps?.teacher}
        </p>
        <p className="no-margin-block padding-inline-10">
          {eventInfo.timeText}
        </p>
      </div>
    );
  };

  useEffect(() => {
    getAllTerms();
  }, []);

  // Fetch periods when active term is available and class ID is ready
  useEffect(() => {
    if (activeTerm && getCurrentClassId()) {
      getAllPeriods();
    }
  }, [activeTerm, getCurrentClassId()]);

  /**
   * Sets the time slot configuration depending on the calendar view (mobile or not).
   * Adjusts min and max times shown on the calendar.
   * This effect runs whenever the mobile state changes.
   * @dependency mobile
   */
  useEffect(() => {
    if (renderView() === "timeGridDay") {
      setTimeConfig({
        slotMinTime: "06:00:00", // Earlier start to catch early classes
        slotMaxTime: "20:00:00"  // Later end to catch late classes
      });
    } else {
      setTimeConfig({
        slotMinTime: "06:00:00",
        slotMaxTime: "20:00:00"
      });
    }
  }, [mobile]);

  return (
    <Layout children={
      <>
        <PageTitle
          title={`${selectedWard?.first_name} ${selectedWard?.last_name} Timetable`}
          subtitle="See all my timetable"
          button={
            <div className="flex gap-10">
              <PopModal videoUrl="https://youtu.be/g3qI35QZuS8" title="Time Table" modalKey="timetable" />
            </div>
          }
        />
        <div className="page-content">
          <div className="padding-left-16">
            <div className={`${classes.terminfo}`}>
              <div className="flex gap-10 justify-content-between">
                <p className="text-align-left">Current Term: </p>
                <p>{activeTerm?.term}</p>
              </div>
              <div className="flex gap-10 justify-content-between">
                <p>Starts On: </p>
                <p>{activeTerm?.start_date?.substring(0, 10)}</p>
              </div>
              <div className="flex gap-10 justify-content-between">
                <p>Ends on: </p>
                <p>{activeTerm?.end_date?.substring(0, 10)}</p>
              </div>
            </div>
          </div>

          <div className={classes.calender}>
            <Calendar
              events={events}
              renderEventContent={(eventInfo) => renderEventContent(eventInfo, mobile)}
              defaultView={renderView()}
              minTime={timeConfig?.slotMinTime}
              maxTime={timeConfig?.slotMaxTime}
              handleEventClick={false}
            />

          </div>
        </div>
        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default StudentTimeTable;
